// Pure Radar layer (04h-4a) — the axes, population scaling, and chart geometry ported
// verbatim from js/renderers-radar.js. Kept pure (population + catalogue passed in, no DOM,
// no D3) so the correctness-critical parts — which metrics an axis list holds, where a
// marker sits on its population-scaled spoke, and the chart's angle/point math — are
// unit-tested. The D3 drawing itself (04h-4b) is a thin imperative island over these.
//
// Phase 02c's useRadarData already lands the per-player value merge (radarMetrics over
// stats); this adds what 02c deferred as "renderer logic": TEMPLATE_AXIS_ORDER + axis
// resolution, and the per-template population range scaling (the shared "all players by
// template" read radarRange needs). Distribution-only helpers (percentile rank, kernel
// density, template population count) are deferred to 04h-5.

import type { ProtoMetricCategory, ProtoPlayerMetric } from "@catbento/shared";
import type { RawPlayerRecord } from "../data/snapshot.js";

export interface RadarAxis {
  key: string;
  label: string;
  outputType: string;
}
export interface RadarRange {
  min: number;
  max: number;
}
export type RadarAxisWithRange = RadarAxis & RadarRange;
export interface RadarPoint {
  x: number;
  y: number;
}

/** The fixed 6-option template list Catalyst's Radar page offers, in this exact order — a user
 *  views a player through any template's axis set, independent of their detected position.
 *  renderers-radar.js:1139. */
export const TEMPLATE_ORDER = ["striker", "attacking_midfielder", "midfielder", "centre_back", "full_back", "goalkeeper"];

/** "full_back" → "Full Back". A per-file helper in the legacy (renderers-radar.js:16); shared here
 *  so both radar renderers (single-player + comparison) label templates the same way. */
export function humanizePositionTemplate(tpl: string): string {
  if (!tpl) return "";
  return tpl.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

/** key → catalogue metric, flattened across categories/subcategories
 *  (renderers-radar.js metricCatalogLookup). */
export function buildMetricCatalog(categories: readonly ProtoMetricCategory[]): Record<string, ProtoPlayerMetric> {
  const catalog: Record<string, ProtoPlayerMetric> = {};
  for (const c of categories) for (const s of c.subcategories) for (const m of s.playerMetrics) catalog[m.key] = m;
  return catalog;
}

// The exact per-template axis order Catalyst renders, transcribed from its own reference
// screenshots (NOT derived from population-scan order — they don't match). Excludes Minutes
// (shown separately, in the table only) and the raw non-per-90 "Total" duplicates.
// renderers-radar.js:52 — kept byte-for-byte.
export const TEMPLATE_AXIS_ORDER: Record<string, string[]> = {
  midfielder: ["obv_90", "deep_progressions_90", "obv_pass_90", "op_xga_90", "obv_dribble_carry_90", "fouls_won_90", "turnovers_90", "padj_pressures_90", "padj_tackles_and_interceptions_90", "challenge_perc", "obv_defensive_action_90"],
  centre_back: ["aerial_perc", "aerials_won_90", "obv_90", "obv_pass_90", "change_in_passing_perc", "obv_dribble_carry_90", "fouls_committed_90", "padj_clearances_90", "blocks_per_shot", "challenge_perc", "obv_defensive_action_90"],
  full_back: ["padj_tackles_and_interceptions_90", "deep_progressions_90", "obv_pass_90", "op_xga_90", "obv_dribble_carry_90", "turnovers_90", "aerial_perc", "padj_pressures_90", "fouls_committed_90", "challenge_perc", "obv_defensive_action_90"],
  goalkeeper: ["obv_gk_90", "long_ball_perc", "pass_into_danger_perc", "claim_success_perc", "gk_average_x_aggressive_action", "np_save_perc", "np_xg_positioning_suppression_90", "np_xg_stopping_suppression_90", "np_xg_save_difficulty_faced_90", "np_xg_faced_90"],
  attacking_midfielder: ["np_xg_90", "np_shots_90", "obv_shot_90", "padj_pressures_90", "obv_pass_90", "op_xga_90", "obv_dribble_carry_90", "fouls_won_90", "turnovers_90", "touches_in_box_90", "np_xg_per_shot"],
  striker: ["np_xg_90", "np_shots_90", "obv_shot_90", "obv_pass_90", "op_xga_90", "padj_pressures_90", "aerials_won_90", "turnovers_90", "obv_dribble_carry_90", "touches_in_box_90", "np_xg_per_shot"],
};

// Build axis objects from a key list. Uses the catalogue's FULL `name` (not shortName) —
// Catalyst's reference table spells these out. renderers-radar.js:61/87.
function axesFromKeys(keys: readonly string[], catalog: Record<string, ProtoPlayerMetric>): RadarAxis[] {
  return keys.map((key) => {
    const cat = catalog[key];
    return { key, label: cat ? cat.name : key, outputType: cat ? cat.outputType : "decimal" };
  });
}

/** A template's default axis list. */
export function radarAxesForTemplate(template: string, catalog: Record<string, ProtoPlayerMetric>): RadarAxis[] {
  return axesFromKeys(TEMPLATE_AXIS_ORDER[template] ?? [], catalog);
}

/** The active axis list: a module's custom axes (mod._radarCustomAxes) override the template
 *  default whenever set, else the template default. renderers-radar.js:87. */
export function resolvedAxes(
  template: string,
  customKeys: readonly string[] | null | undefined,
  catalog: Record<string, ProtoPlayerMetric>,
): RadarAxis[] {
  if (!customKeys || !customKeys.length) return radarAxesForTemplate(template, catalog);
  return axesFromKeys(customKeys, catalog);
}

/** Minutes appended for the companion table only (never a chart axis). renderers-radar.js:126. */
export function withMinutesRow(axes: readonly RadarAxis[], catalog: Record<string, ProtoPlayerMetric>): RadarAxis[] {
  const cat = catalog.minutes;
  return axes.concat([{ key: "minutes", label: cat ? cat.name : "Minutes", outputType: cat ? cat.outputType : "integer" }]);
}

/** A player's raw value for one axis in a season: radarMetrics wins (the template's own
 *  authoritative capture), else the flat stats value. renderers-radar.js:104. */
export function axisRawValue(p: RawPlayerRecord, seasonCode: string, key: string): number | null | undefined {
  const season = p.seasons?.[seasonCode] ?? {};
  const rm = season.radarMetrics ?? {};
  if (rm[key] !== undefined) return rm[key];
  return (season.stats ?? {})[key];
}

/** One player's values for a whole axis list (keyed by axis.key). renderers-radar.js:115. */
export function axisValuesMap(
  p: RawPlayerRecord,
  seasonCode: string,
  axes: readonly RadarAxis[],
): Record<string, number | null | undefined> {
  const map: Record<string, number | null | undefined> = {};
  for (const a of axes) map[a.key] = axisRawValue(p, seasonCode, a.key);
  return map;
}

/** Real per-axis population values WITHIN the same position template, sorted ascending — each
 *  player read at their own currentSeason through the same radarMetrics-first fallback as the
 *  marker, so a range never disagrees with the marker drawn on it. renderers-radar.js:143.
 *  (Population passed in — pure/data-injected, like the Stats engine.) */
export function radarPopulationValues(
  population: readonly RawPlayerRecord[],
  template: string,
  key: string,
): number[] {
  const values: number[] = [];
  for (const p of population) {
    if (p.positionTemplate !== template) continue;
    const v = axisRawValue(p, p.currentSeason ?? "", key);
    if (v !== undefined && v !== null && !Number.isNaN(v)) values.push(v);
  }
  return values.sort((a, b) => a - b);
}

/** Min/max for a metric within a template — the same idea as Stats' fullRange, partitioned by
 *  template (a full-back's tackles have no business scaled against strikers). renderers-radar.js:161. */
export function radarRange(population: readonly RawPlayerRecord[], template: string, key: string): RadarRange {
  const values = radarPopulationValues(population, template, key);
  return values.length ? { min: values[0]!, max: values[values.length - 1]! } : { min: 0, max: 0 };
}

// Metrics where a LOWER value is better — their marker fraction is inverted so "good" always
// reads as further-out on the spoke, and colour cues stay honest. renderers-radar.js:248.
export const LOWER_IS_BETTER_METRICS = new Set<string>([
  "turnovers_90", "fouls_committed_90", "yellow_cards_90", "second_yellow_cards_90",
  "red_cards_90", "dispossessions_90", "dribbled_past_90", "failed_dribbles_90",
  "errors_90", "pressured_action_fails_90", "counterpressured_action_fails_90",
  "pass_into_danger_perc", "goals_faced_90", "penalty_goals_faced_90",
]);
export function isLowerBetter(key: string): boolean {
  return LOWER_IS_BETTER_METRICS.has(key);
}

/** Where a raw value sits on its spoke, 0–1 (inverted for lower-is-better metrics), or null
 *  when there's no value. Degenerate range → mid-spoke. renderers-radar.js:256. Range is
 *  passed in (resolve it via radarRange) so this stays pure. */
export function metricFraction(rawValue: number | null | undefined, range: RadarRange, lowerBetter: boolean): number | null {
  if (rawValue === undefined || rawValue === null || Number.isNaN(rawValue)) return null;
  const { min, max } = range;
  const frac = max > min ? Math.max(0, Math.min(1, (rawValue - min) / (max - min))) : 0.5;
  return lowerBetter ? 1 - frac : frac;
}

/** Attach each axis's real min/max (the hover tooltip + table need it). renderers-radar.js:267. */
export function axesWithRange(
  population: readonly RawPlayerRecord[],
  template: string,
  axes: readonly RadarAxis[],
): RadarAxisWithRange[] {
  return axes.map((a) => ({ ...a, ...radarRange(population, template, a.key) }));
}

// --- Chart geometry (renderers-radar.js:272) ---------------------------------
export const CHART_SIZE = 280;
export const CHART_CENTER = CHART_SIZE / 2;
export const CHART_RADIUS = 115;
export const RING_BAND_COUNT = 10;
// Largest-to-smallest so each smaller filled band layers on top (the alternating "dartboard").
export const RING_BAND_FRACS = Array.from({ length: RING_BAND_COUNT }, (_, i) => 1 - i / RING_BAND_COUNT);
export const CHART_CENTER_HOLE_RADIUS = 14;
export const AXIS_LABEL_MAX_LEN = 16;
// Ticks on every OTHER ring boundary (halves the label count, always includes the outer max).
export const TICK_FRACS = Array.from({ length: RING_BAND_COUNT / 2 }, (_, i) => (2 * (i + 1)) / RING_BAND_COUNT);

/** Angle of the i-th of n axes, starting at 12 o'clock, clockwise. */
export function axisAngle(i: number, n: number): number {
  return -Math.PI / 2 + (i * 2 * Math.PI) / n;
}
/** A point at `frac` (0–1) along a spoke, in the chart's viewBox coords. */
export function axisPoint(angle: number, frac: number): RadarPoint {
  return {
    x: CHART_CENTER + Math.cos(angle) * CHART_RADIUS * frac,
    y: CHART_CENTER + Math.sin(angle) * CHART_RADIUS * frac,
  };
}
/** translate() percentages that anchor an HTML label's text-anchor point at a spoke end —
 *  the CSS equivalent of SVG text-anchor/dominant-baseline (labels are HTML, not SVG, so the
 *  viewBox scale never balloons their font). renderers-radar.js:312. */
export function labelTransformPct(angle: number): string {
  const x = Math.cos(angle);
  const y = Math.sin(angle);
  const tx = Math.abs(x) < 0.15 ? -50 : x > 0 ? 0 : -100;
  const ty = Math.abs(y) < 0.15 ? -50 : y > 0 ? 0 : -100;
  return `translate(${tx}%, ${ty}%)`;
}
/** Outer labels tilt tangent to the ring, flipping 180° on the lower half to stay upright.
 *  renderers-radar.js:336. */
export function labelRotationDeg(angle: number): number {
  let deg = (angle * 180) / Math.PI + 90;
  deg = ((deg % 360) + 360) % 360;
  if (deg > 90 && deg < 270) deg -= 180;
  return deg;
}
/** Cap an axis label so full metric names don't run into the next spoke. renderers-radar.js:348. */
export function truncateLabel(text: string, maxLen: number = AXIS_LABEL_MAX_LEN): string {
  return text.length > maxLen ? text.slice(0, maxLen) + "..." : text;
}
/** The invisible-until-hovered pie wedge for one axis, spanning half the angular gap to each
 *  neighbour and reaching the outer ring. renderers-radar.js:369. */
export function wedgePath(angle: number, n: number): string {
  const halfWidth = Math.PI / n;
  const p1 = axisPoint(angle - halfWidth, 1);
  const p2 = axisPoint(angle + halfWidth, 1);
  return `M ${CHART_CENTER},${CHART_CENTER} L ${p1.x.toFixed(2)},${p1.y.toFixed(2)} A ${CHART_RADIUS},${CHART_RADIUS} 0 0 1 ${p2.x.toFixed(2)},${p2.y.toFixed(2)} Z`;
}

/** The `points` string for one player's data polygon — a vertex per axis at that axis's
 *  fraction, skipping axes with no value (null) rather than faking a 0th-percentile point.
 *  Fewer than 3 real vertices can't form a shape, so return "" (the chart draws nothing).
 *  renderers-radar.js:1075. Pure — the D3 island (04h-4b) just sets this on the polygon. */
export function polygonPoints(axes: readonly RadarAxis[], fractions: readonly (number | null)[]): string {
  const n = axes.length;
  const coords: string[] = [];
  fractions.forEach((frac, i) => {
    if (frac == null) return;
    const pt = axisPoint(axisAngle(i, n), frac);
    coords.push(`${pt.x.toFixed(2)},${pt.y.toFixed(2)}`);
  });
  return coords.length >= 3 ? coords.join(" ") : "";
}

// --- Distribution view — the population-density layer (04h-5a) ----------------
// The Distribution view plots the whole population's density for each axis (a kernel-density
// curve) and marks where this player's value falls within it — a second lens on the same axes.
// These are the pieces 04h-4a deferred: the population reads (percentile, template count), the
// Gaussian KDE, and the row geometry. Pure (population/values passed in), so they're unit-tested;
// the renderers (04h-5b single-player, 04h-5d overlay) are thin over these.

export const DIST_ROW_W = 200;
export const DIST_ROW_H = 34;
export const DIST_BASELINE_Y = DIST_ROW_H - 2;
export const DIST_TOP_Y = 3;

/** One sample of a density curve, positioned as a fraction (0–1) across the row. */
export interface DensityPoint {
  frac: number;
  density: number;
}

/** How many players sit at this position template — the Distribution header's "N players".
 *  renderers-radar.js:173 (population passed in). */
/** What a Distribution row is drawn against: each stat's values across the group, lowest first,
 *  which way is better, and the header's words. A player's group is everyone at their position
 *  (playerSpread); a team's, the teams in its league (live/team-radar.ts teamSpread). */
export interface DistSpread {
  valuesOf: (key: string) => number[];
  lowerBetter: (key: string) => boolean;
  /** "vs Strikers · 434 players". */
  sub: string;
}

/** The player Radar's spread: every player at the template's position. Pure. */
export function playerSpread(population: readonly RawPlayerRecord[], template: string, templateLabel: string): DistSpread {
  return {
    valuesOf: (key) => radarPopulationValues(population, template, key),
    lowerBetter: isLowerBetter,
    sub: `vs ${templateLabel}s · ${templatePopulationCount(population, template)} players`,
  };
}

export function templatePopulationCount(population: readonly RawPlayerRecord[], template: string): number {
  let n = 0;
  for (const p of population) if (p.positionTemplate === template) n++;
  return n;
}

/** Share of the population at or below `value`, 0–100 — a plain count-based rank (the population
 *  per template is small enough not to need interpolation). `sortedValues` ascending, but only the
 *  count matters. renderers-radar.js:181. */
export function percentileRank(sortedValues: readonly number[], value: number | null | undefined): number | null {
  if (!sortedValues.length || value == null || Number.isNaN(value)) return null;
  let count = 0;
  for (const v of sortedValues) if (v <= value) count++;
  return Math.round((count / sortedValues.length) * 100);
}

/** A Gaussian kernel-density estimate over the population values — Silverman's rule-of-thumb
 *  bandwidth — sampled at `numPoints` evenly across [domainMin, domainMax], density normalized so
 *  the tallest point is 1 (callers only need the curve's SHAPE). renderers-radar.js:194. */
export function kernelDensityPoints(
  values: readonly number[],
  domainMin: number,
  domainMax: number,
  numPoints: number,
): { x: number; density: number }[] {
  const n = values.length;
  if (!n || domainMax <= domainMin) return [];
  const mean = values.reduce((a, b) => a + b, 0) / n;
  const variance = values.reduce((a, b) => a + (b - mean) * (b - mean), 0) / n;
  const std = Math.sqrt(variance);
  const bandwidth = Math.max(1.06 * std * Math.pow(n, -0.2), (domainMax - domainMin) / 200) || (domainMax - domainMin) / 20;
  const raw: { x: number; density: number }[] = [];
  let maxDensity = 0;
  for (let i = 0; i < numPoints; i++) {
    const x = domainMin + (i / (numPoints - 1)) * (domainMax - domainMin);
    let sum = 0;
    for (const v of values) {
      const u = (x - v) / bandwidth;
      sum += Math.exp(-0.5 * u * u);
    }
    const density = sum / (n * bandwidth * Math.sqrt(2 * Math.PI));
    if (density > maxDensity) maxDensity = density;
    raw.push({ x, density });
  }
  return raw.map((pt) => ({ x: pt.x, density: maxDensity > 0 ? pt.density / maxDensity : 0 }));
}

/** The ordered density curve as {frac, density} across the row — mirrored (density sequence
 *  reversed) for a lower-is-better metric so the "good" end is always on the right. `values`
 *  ascending (from radarPopulationValues). renderers-radar.js:840. */
export function densityCurve(values: readonly number[], lowerBetter: boolean, numPoints = 48): DensityPoint[] {
  const domainMin = values.length ? values[0]! : 0;
  const domainMax = values.length ? values[values.length - 1]! : 0;
  const raw = kernelDensityPoints(values, domainMin, domainMax, numPoints);
  const ordered = lowerBetter ? raw.slice().reverse() : raw;
  return ordered.map((pt, i, arr) => ({ frac: i / (arr.length - 1 || 1), density: pt.density }));
}

/** A raw value's marker position along the row (0–1) — mirrored for lower-is-better, 0.5 for a
 *  degenerate domain. Unclamped (the readout uses it as-is; distAreaPaths clamps). rrjs:845. */
export function distMarkerFrac(rawValue: number, domainMin: number, domainMax: number, lowerBetter: boolean): number {
  const raw = domainMax > domainMin ? (rawValue - domainMin) / (domainMax - domainMin) : 0.5;
  return lowerBetter ? 1 - raw : raw;
}

/** The y (in row coords) of a marker sitting on the curve at `frac` — the nearest sampled point's
 *  height. Used by the overlay's per-player markers. renderers-radar.js:918. */
export function distMarkerY(points: readonly DensityPoint[], frac: number): number {
  if (!points.length) return DIST_BASELINE_Y;
  const idx = Math.max(0, Math.min(points.length - 1, Math.round(frac * (points.length - 1))));
  return DIST_BASELINE_Y - points[idx]!.density * (DIST_BASELINE_Y - DIST_TOP_Y);
}

function distToXY(pt: DensityPoint): [number, number] {
  return [pt.frac * DIST_ROW_W, DIST_BASELINE_Y - pt.density * (DIST_BASELINE_Y - DIST_TOP_Y)];
}

/** A single filled area under the whole density curve — the no-marker single-area case (an axis
 *  the player has no value for, and the overlay's neutral curve). Starts at x=0. rrjs:770/899. */
export function distSingleAreaPath(points: readonly DensityPoint[]): string {
  if (!points.length) return "";
  const coords = points.map(distToXY);
  const last = coords[coords.length - 1]!;
  return `M 0,${DIST_BASELINE_Y} L ${coords.map((c) => c[0].toFixed(1) + "," + c[1].toFixed(1)).join(" L ")} L ${last[0].toFixed(1)},${DIST_BASELINE_Y} Z`;
}

/** Split the density curve at the marker's x into a below/above area (a clean vertical seam at the
 *  marker, with a point interpolated exactly there), plus the marker's x/y. The single-player row's
 *  two-tone neutral fill. `markerFrac` clamped to [0,1] by the caller. renderers-radar.js:693. */
export function distAreaPaths(
  points: readonly DensityPoint[],
  markerFrac: number,
): { leftPath: string; rightPath: string; markerX: number; markerY: number } {
  const withMarker: DensityPoint[] = [];
  for (let i = 0; i < points.length; i++) {
    withMarker.push(points[i]!);
    const next = points[i + 1];
    if (next && points[i]!.frac < markerFrac && markerFrac < next.frac) {
      const t = (markerFrac - points[i]!.frac) / (next.frac - points[i]!.frac);
      withMarker.push({ frac: markerFrac, density: points[i]!.density + t * (next.density - points[i]!.density) });
    }
  }
  const areaPath = (pts: DensityPoint[]): string => {
    if (pts.length < 2) return "";
    const coords = pts.map(distToXY);
    const first = coords[0]!;
    const last = coords[coords.length - 1]!;
    return `M ${first[0].toFixed(1)},${DIST_BASELINE_Y} L ${coords.map((c) => c[0].toFixed(1) + "," + c[1].toFixed(1)).join(" L ")} L ${last[0].toFixed(1)},${DIST_BASELINE_Y} Z`;
  };
  const left = withMarker.filter((pt) => pt.frac <= markerFrac);
  const right = withMarker.filter((pt) => pt.frac >= markerFrac);
  const markerX = markerFrac * DIST_ROW_W;
  const markerY = DIST_BASELINE_Y - (withMarker.find((pt) => pt.frac === markerFrac) ?? { density: 0 }).density * (DIST_BASELINE_Y - DIST_TOP_Y);
  return { leftPath: areaPath(left), rightPath: areaPath(right), markerX, markerY };
}
